import React, { useState } from "react";
import {
  LayoutDashboard, Users as UsersIcon, Trophy, Building2, Megaphone, ShieldCheck,
  CalendarRange, GraduationCap, Receipt, CreditCard, Settings as SettingsIcon,
  History, Tag, MapPin, Bell,
} from "lucide-react";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../contexts/AuthContext";

import OverviewSection from "./admin/OverviewSection";
import UsersSection from "./admin/UsersSection";
import SportsSection from "./admin/SportsSection";
import ClubsSection from "./admin/ClubsSection";
import EventsSection from "./admin/EventsSection";
import InvoicesSection from "./admin/InvoicesSection";
import PaymentsSection from "./admin/PaymentsSection";
import BroadcastSection from "./admin/BroadcastSection";
import SettingsSection from "./admin/SettingsSection";
import AuditSection from "./admin/AuditSection";
import FacilityClaimsSection from "./admin/FacilityClaimsSection";
import FacilitiesAdminSection from "./admin/FacilitiesSection";
import WatchersSection from "./admin/WatchersSection";

const SECTIONS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: UsersIcon },
  { id: "sports", label: "Sports", icon: Tag },
  { id: "clubs", label: "Clubs", icon: Building2 },
  { id: "matches", label: "Matches", icon: CalendarRange },
  { id: "tournaments", label: "Tournaments", icon: Trophy },
  { id: "leagues", label: "Leagues", icon: Trophy },
  { id: "lessons", label: "Lessons", icon: GraduationCap },
  { id: "claims", label: "Facility claims", icon: ShieldCheck },
  { id: "facilities", label: "Facilities", icon: MapPin },
  { id: "invoices", label: "Invoices", icon: Receipt },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "broadcast", label: "Broadcast", icon: Megaphone },
  { id: "watchers", label: "Watchers", icon: Bell },
  { id: "settings", label: "Settings", icon: SettingsIcon },
  { id: "audit", label: "Audit log", icon: History },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [section, setSection] = useState("overview");

  return (
    <AppLayout>
      <div className="grid grid-cols-12 gap-6">
        <aside className="col-span-12 lg:col-span-3 xl:col-span-2" data-testid="admin-sidebar">
          <div className="pp-card-elevated p-3 sticky top-20">
            <div className="px-3 py-2 mb-2">
              <div className="font-heading text-lg font-black flex items-center gap-2"><ShieldCheck size={18} className="text-[#00E676]" /> Admin C-Panel</div>
              <div className="text-xs text-zinc-500 mt-1">{user?.email}</div>
            </div>
            <nav className="space-y-0.5">
              {SECTIONS.map((s) => {
                const Ic = s.icon;
                const active = s.id === section;
                return (
                  <button
                    key={s.id}
                    data-testid={`cp-nav-${s.id}`}
                    onClick={() => setSection(s.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/30" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}
                  >
                    <Ic size={15} /> {s.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        <section className="col-span-12 lg:col-span-9 xl:col-span-10" data-testid="admin-main">
          {section === "overview" && <OverviewSection />}
          {section === "users" && <UsersSection />}
          {section === "sports" && <SportsSection />}
          {section === "clubs" && <ClubsSection />}
          {section === "matches" && <EventsSection kind="match" />}
          {section === "tournaments" && <EventsSection kind="tournament" verifiable />}
          {section === "leagues" && <EventsSection kind="league" verifiable />}
          {section === "lessons" && <EventsSection kind="lesson" />}
          {section === "claims" && <FacilityClaimsSection />}
          {section === "facilities" && <FacilitiesAdminSection />}
          {section === "invoices" && <InvoicesSection />}
          {section === "payments" && <PaymentsSection />}
          {section === "broadcast" && <BroadcastSection />}
          {section === "watchers" && <WatchersSection />}
          {section === "settings" && <SettingsSection />}
          {section === "audit" && <AuditSection />}
        </section>
      </div>
    </AppLayout>
  );
}
